import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { $Values } from 'utility-types';
import { scrollTo } from '@daedalus/core/src/_web/utils/browser';
import useWindowSize from '@daedalus/core/src/_web/utils/browser/hooks/useWindowSize';
import { useDeviceLayout } from '../../../context/deviceLayout';
import { Portal } from '../helpers/Portal';
import useBodyScrollLock from './useBodyScrollLock';
export { BODY_SCROLL_LOCK_IGNORE } from './useBodyScrollLock';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import noop from 'lodash/noop';
import { usePreviousValue } from '@daedalus/core/src/utils/react/hooks/usePreviousValue';
import { KEYCODE_ESC, KEYSTRING_ESC, KEYSTRING_SHORT_ESC } from '../../../utils/closeOnEscape';
import transitions from '../../../utils/framerTransitions';
export type AnimationType = $Values<typeof ANIMATION_TYPES>;
type StatusType = $Values<typeof STATUSES>;
interface Props {
  /** The body of the overlay */
  children: ReactNode;
  /** Whether the overlay is open */
  isOpen?: boolean;
  /** The styles for the overlay container */
  containerStyles?: React.CSSProperties;
  /** The styles for the overlay content. It's a classname from Linaria. */
  customStyles?: string;
  /** Whether to add a shade to the background */
  shadeBackground?: boolean;
  /** Callback when user clicks on the background shadow */
  onShadeBackgroundClick?: () => void;
  /** The style of animation for opening and closing */
  animationType?: AnimationType;
  /** Whether the overlay should call the onClose callback when the Esc key is pressed */
  closeOnEsc?: boolean;
  /** The callback for closing the overlay. Optionally pass in the element that was selected to close. */
  onClose?: (e?: React.SyntheticEvent, element?: string) => void;
  /** Enable body scroll lock  */
  enableBodyScrollLock?: boolean;
  /**
   * Enable content background hack.
   * On iOS the viewport height lags after the navbar hiding on scroll, this means the content behind the overlay is shown while the navbar animates out
   * This is a bandaid solution, putting a white background under the overlay content that extends under the nav bar
   *
   * It's advisable to enable this for fullscreen overlays with a busy page underneath
   */
  enableContentBackgroundHack?: boolean;
  /** How long the animation will last in milliseconds */
  transitionDuration?: number;
  /** Pass through classname to allow styles overrides */
  className?: string;
  /** Pass through style to allow styles overrides */
  style?: React.CSSProperties;
  /**
   *  By passing `animatePresenceInitial={false}`, `AnimatePresence` will disable any initial animations on children that are present when the component is first rendered.
   * **/
  animatePresenceInitial?: boolean;
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string;
}
const STATUSES = {
  INITIAL: 'INITIAL',
  RENDERED: 'RENDERED',
  VISIBLE: 'VISIBLE',
  HIDDEN: 'HIDDEN'
} as const;
export const ANIMATION_TYPES = {
  NONE: 'none',
  SLIDE_LEFT: 'slideLeft',
  SLIDE_RIGHT: 'slideRight',
  SLIDE_UP: 'slideUp',
  SLIDE_DOWN: 'slideDown',
  FADE_IN_OUT: 'overlayFade'
} as const;
export const TRANSITION_DURATION = 300; // ms
const SCROLL_TOP_DURATION = 250; // ms
const INSTANT_TRANSITION_DURATION = 0; // ms

const OverlayContainer = /*#__PURE__*/styled('div')({
  name: "OverlayContainer",
  class: "ouxj3iu",
  propsAsIs: false
});
const _exp = /*#__PURE__*/() => motion.div;
export const OverlayContent = /*#__PURE__*/styled(_exp())({
  name: "OverlayContent",
  class: "o1n06xk1",
  propsAsIs: true
});
const _exp4 = /*#__PURE__*/() => motion.div;
export const OverlayBackground = /*#__PURE__*/styled(_exp4())({
  name: "OverlayBackground",
  class: "o1kv691y",
  propsAsIs: true
});
const _exp6 = /*#__PURE__*/() => motion.div;
const ContentBackgroundHack = /*#__PURE__*/styled(_exp6())({
  name: "ContentBackgroundHack",
  class: "ciniavt",
  propsAsIs: true
});
export interface OverlayContextValue {
  scrollToTop: () => void;
}
export const OverlayContext = React.createContext<OverlayContextValue | undefined>(undefined);
export const useOverlayHandlers = () => {
  const context = useContext(OverlayContext);
  if (context === undefined) {
    throw new Error('useOverlayHandlers must be within OverlayContext Provider');
  }
  return context;
};
const FullHeightContainer = ({
  children,
  style,
  containerStyles,
  className,
  dataId
}: {
  children: ReactNode;
  containerStyles: Props['containerStyles'];
  className?: string;
  style?: React.CSSProperties;
  dataId?: string;
}) => {
  // Use the true viewport-height since CSS 100% / 100vh etc. break on on iOS Chrome
  // There is a layout bug on iOS Chrome when opening a tab by clicking a link that causes the viewport extend under the navbar (interacting with the URL bar will fix the layout)
  // This bug does not show up on Browserstack. Test this flow with search overlays on a real device if any changes are made!
  const {
    isMobile
  } = useDeviceLayout();
  const {
    height
  } = useWindowSize();
  const additionalStyles = isMobile ? {
    height: `${height}px`
  } : {};
  return <OverlayContainer style={{
    ...style,
    ...containerStyles,
    ...additionalStyles
  }} className={className} data-id={dataId}>
      {children}
    </OverlayContainer>;
};
const Content = ({
  children,
  className,
  shadeBackground,
  enableBodyScrollLock,
  animationType,
  transitionDuration
}: {
  children: ReactNode;
  className?: string; // Pass through linaria styling
  shadeBackground: Props['shadeBackground'];
  enableBodyScrollLock: Props['enableBodyScrollLock'];
  animationType: AnimationType;
  transitionDuration: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useBodyScrollLock({
    ref,
    isEnabled: enableBodyScrollLock
  });
  const scrollToTop = useCallback((duration = SCROLL_TOP_DURATION) => {
    if (ref.current) scrollTo(ref.current, 0, duration);
  }, []);
  const contextValue: OverlayContextValue = useMemo(() => ({
    scrollToTop
  }), []);
  if (animationType === ANIMATION_TYPES.NONE) {
    return <OverlayContext.Provider value={contextValue}>
        <OverlayContent className={cx(className, shadeBackground && '--shadow')} ref={ref}>
          {children}
        </OverlayContent>
      </OverlayContext.Provider>;
  }
  const transitionFunc = transitions[animationType] || transitions[ANIMATION_TYPES.SLIDE_RIGHT]; // or default animation type if provided one is incorrect

  return <OverlayContext.Provider value={contextValue}>
      <OverlayContent key="OverlayContent" variants={transitionFunc} initial="exited" animate="entered" exit="exited" transition={{
      duration: transitionDuration / 1000
    }} ref={ref} className={cx(className, shadeBackground && '--shadow')}>
        {children}
      </OverlayContent>
    </OverlayContext.Provider>;
};
export const Overlay = ({
  transitionDuration = TRANSITION_DURATION,
  animationType = ANIMATION_TYPES.SLIDE_RIGHT,
  closeOnEsc = false,
  shadeBackground = true,
  enableBodyScrollLock = true,
  enableContentBackgroundHack = false,
  onShadeBackgroundClick = noop,
  isOpen,
  onClose,
  customStyles,
  children,
  containerStyles,
  className,
  style,
  animatePresenceInitial = false,
  dataId
}: Props) => {
  const isOpenPrevious = usePreviousValue(isOpen);
  const [status, setStatus] = useState<StatusType>(STATUSES.INITIAL);
  // eslint-disable-next-line no-undef
  const timeout = useRef<NodeJS.Timeout>();
  const isInstant = animationType === ANIMATION_TYPES.NONE;
  const overrideTransitionDuration = isInstant ? INSTANT_TRANSITION_DURATION : transitionDuration;
  const handleCloseOnEscapeKey = useCallback((e: KeyboardEvent) => {
    if (closeOnEsc && isOpen && (e.key === KEYSTRING_ESC || e.key === KEYSTRING_SHORT_ESC || e.keyCode === KEYCODE_ESC)) {
      onClose?.();
    }
  }, [closeOnEsc, isOpen, onClose]);
  useEffect(() => {
    window.addEventListener('keydown', handleCloseOnEscapeKey, true);
    return () => {
      window.removeEventListener('keydown', handleCloseOnEscapeKey, true);
    };
  }, [handleCloseOnEscapeKey, onClose]);
  const openOverlay = () => {
    if (timeout.current) clearTimeout(timeout.current);
    setStatus(STATUSES.RENDERED);
    window.requestAnimationFrame(() => {
      setStatus(STATUSES.VISIBLE);
    });
  };
  const closeOverlay = () => {
    if (timeout.current) clearTimeout(timeout.current);
    setStatus(STATUSES.HIDDEN);
    timeout.current = setTimeout(() => {
      setStatus(STATUSES.INITIAL);
    }, overrideTransitionDuration);
  };
  useEffect(() => {
    const shouldOpenOverlay = status === STATUSES.INITIAL && !isOpenPrevious && isOpen;
    const shouldCloseOverlay = isOpenPrevious && !isOpen;
    if (shouldOpenOverlay) openOverlay();
    if (shouldCloseOverlay) closeOverlay();
  }, [status, timeout, isOpen, isOpenPrevious]);
  const backgroundTransition = transitions.overlayFade;
  if (status === STATUSES.INITIAL) return null;
  return <Portal>
      <AnimatePresence initial={animatePresenceInitial}>
        {status === STATUSES.VISIBLE && <>
            {enableContentBackgroundHack && <ContentBackgroundHack key="OverlayBackgroundHack" initial="exited" animate="entered" exit="exited" variants={backgroundTransition} />}
            <FullHeightContainer containerStyles={containerStyles} className={className} style={style} dataId={dataId}>
              {shadeBackground && <OverlayBackground key="OverlayBackground" variants={backgroundTransition} initial="exited" animate="entered" exit="exited" onClick={onShadeBackgroundClick} className={cx(shadeBackground && '--shadow')} />}
              <Content className={customStyles} shadeBackground={shadeBackground} enableBodyScrollLock={enableBodyScrollLock} animationType={animationType} transitionDuration={overrideTransitionDuration}>
                {children}
              </Content>
            </FullHeightContainer>
          </>}
      </AnimatePresence>
    </Portal>;
};

require("./Overlay.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Overlay.tsx");